import {BehaviorSubject, combineLatest, forkJoin, from, Observable, of, Subject, throwError} from 'rxjs';
import {
    ActivityExecutionStatus,
    AssessmentEvaluationStatus,
    AssessmentTask,
    Audit,
    AuditActivity,
    AuditService,
    HydratedAuditTarget,
    PlanningMatrix,
} from '@webmate-web-core/shared/services/audit.service';
import {ArtifactService} from '@webmate-web-core/shared/services/artifact.service';
import {TestService} from '@webmate-web-core/shared/services/test.service';
import {AuditActivityId, AuditId, FindingId, ProjectId, SpecId, TestObjectId} from '@webmate-web-core/shared/types/ids';
import {catchError, map, mergeMap, startWith, switchMap, take, tap} from 'rxjs/operators';
import {AssignmentSpec, Finding, FindingInfo, FindingsStats, ObservationTarget, Spec, StateObservationTarget} from '@webmate-web-core/shared/types/testmgmt';
import {BrowserSessionStateArtifact} from '@webmate-web-core/shared/types/browsersession';
import {SelectItem} from 'primeng/api';
import {tagToString} from '@webmate-web-core/shared/types/tag';
import {AuditRequirement} from '@webmate-web-core/shared/tools/audit/audittemplate/shared';
import {AuditActivityUpdate} from '@webmate-web-core/shared/types/audit';
import {alphanumericDisplayIdSort, mapSpecLabelsToAxeLabels} from '@webmate-web-core/shared/services/specs';

export interface AuditInfo {
    audit?: Audit;
    assessmentTasks?: AssessmentTask[];
    idToAssessmentTask?: { [id: string]: AssessmentTask };
    testObjects?: HydratedAuditTarget[];
    auditRequirements?: AuditRequirement[];
    specs?: { [specId: string]: Spec };
    testObjectsById?: { [testObjectId: string]: HydratedAuditTarget };
    statesByTestObjectId?: { [testObjectId: string]: BrowserSessionStateArtifact };
    availableTestObjects?: SelectItem[];
    availableTags?: SelectItem[];
    availableExecutionStatuses?: SelectItem[];
    availableEvaluationStatuses?: SelectItem[];
    findingStatsBySpecIdAndTestObjectId: Record<string, Record<string, FindingsStats>>;
}

const EMPTY_AUDIT_INFO: AuditInfo = {
    audit: undefined,
    assessmentTasks: [],
    idToAssessmentTask: {},
    testObjects: [],
    auditRequirements: [],
    specs: {},
    testObjectsById: {},
    statesByTestObjectId: {},
    availableTestObjects: [],
    availableTags: [],
    availableExecutionStatuses: [],
    availableEvaluationStatuses: [],
    findingStatsBySpecIdAndTestObjectId: {},
};

export class AuditSession {
    private readonly auditInfo: BehaviorSubject<AuditInfo> = new BehaviorSubject(EMPTY_AUDIT_INFO);

    private readonly updateAuditInfo$ = new Subject<void>();

    constructor(private readonly auditId: AuditId, private readonly auditService: AuditService,
                        private readonly artifactService: ArtifactService, private readonly testService: TestService) {

        if (!!auditId) {
            combineLatest({
                auditInfo: this.updateAuditInfo$.pipe(startWith(undefined), switchMap(_ => this.getAuditData(auditId, true).pipe(take(1)))),
            }).subscribe(({auditInfo}) => {
                this.auditInfo.next(auditInfo);
            });
        }
    }

    public destroy() {
        this.auditInfo.complete();
    }

    /**
     * Updates an 'AuditActivity' in the backend.
     *
     * @param id - of the Activity to update
     * @param updateActions - of the Update Action
     */
    public updateAuditActivity(id: AuditActivityId, updateActions: AuditActivityUpdate[]) {
        const currentAuditInfo = this.auditInfo.getValue();

        this.reifyActivityIfNecessary(id).pipe(
            switchMap(() => this.auditService.updateAuditActivity$(id, updateActions))
        ).subscribe({
            next: (updatedTask) => {
                // replace the task with the updated task
                currentAuditInfo.assessmentTasks = currentAuditInfo.assessmentTasks!.map(task => task.id === id ? updatedTask : task);
                currentAuditInfo.idToAssessmentTask![id] = updatedTask;

                this.auditInfo.next(currentAuditInfo);
            },
            error: (err) => {
                console.error('error setting evaluation status', err);
            }
        });
    }

    /**
     * Ensures that the specified `AssessmentTask` exists in the backend.
     * If the task is still a flyweight object and requires reification, a request is sent to the backend.
     *
     * @param id - The `AuditActivityId` of the `AssessmentTask` to verify or create if necessary.
     * @returns An `Observable<void>` that completes when the reification process is done
     */
    reifyActivityIfNecessary(id: AuditActivityId): Observable<void> {
        const currentAuditInfo = this.auditInfo.getValue();
        const tasksById = currentAuditInfo?.idToAssessmentTask || {};

        if (!tasksById[id]) {
            return of(undefined);
        }

        const task = tasksById[id];
        if (task.needsReification) {
            const testObjectId = task.testObjectId;
            if (!testObjectId) {
                return of(undefined);
            }
            return this.auditService.updateAuditActivity$(task.id, [
                {
                    type: 'ReifyAssessmentTask',
                    testObjectId,
                    assessedSpec: task.assessedSpec,
                    auditId: this.auditId!
                }
            ]).pipe(map(_ => undefined));
        } else {
            return of(undefined);
        }
    }

    public setAssessmentTaskEvaluationStatus(id: AuditActivityId, status: AssessmentEvaluationStatus) {
        this.updateAuditActivity(id, [
            {
                'type': 'SetAssessmentTaskEvaluationStatus',
                evaluationStatus: status
            }
        ]);
    }

    public setAssessmentTaskExecutionStatus(id: AuditActivityId, status: ActivityExecutionStatus) {
        this.updateAuditActivity(id, [
            {
                'type': 'SetAuditActivityExecutionStatus',
                executionStatus: status
            }
        ]);
    }

    public setAssessmentTaskSummary(id: AuditActivityId, text: string) {
        this.updateAuditActivity(id, [
            {
                'type': 'SetSummary',
                summary: text
            }
        ]);
    }

    public getCurrentAuditInfo(): AuditInfo {
        return this.auditInfo.getValue();
    }

    public getAuditInfo$(): Observable<AuditInfo> {
        return this.auditInfo.asObservable();
    }

    private getAuditData(auditId: AuditId, forceRefresh: boolean = false): Observable<AuditInfo> {
        const result: AuditInfo = {
            audit: undefined,
            assessmentTasks: [],
            idToAssessmentTask: {},
            testObjects: [],
            auditRequirements: [],
            specs: {},
            testObjectsById: {},
            statesByTestObjectId: {},
            availableTestObjects: [],
            availableTags: [],
            availableExecutionStatuses: [],
            availableEvaluationStatuses: [],
            findingStatsBySpecIdAndTestObjectId: {},
        };

        return (forceRefresh ? this.auditService.refreshAudit$(auditId) : this.auditService.getAudit$(auditId)).pipe(
            /**
             * Retrieve already created AssessmentTasks from the backend and create the not yet edited ones.
             * These are saved inside the "tasks" list to be able to be used for the next pipe function.
             */
            mergeMap(audit => {
                result.audit = audit;
                return this.computeAssessmentTasks(audit).pipe(
                    map(tasks => ({audit, tasks}))
                );
            }),
            /**
             * Create a Lookup Dictionary for AssessmentTasks.
             * Retrieve Specs from Backend for all the Requirements Data for the next pipe function.
             */
            switchMap(({audit, tasks}) => {
                result.assessmentTasks = tasks;
                result.idToAssessmentTask = tasks.reduce((acc, task) => {
                    acc[task.id] = task;
                    return acc;
                }, {} as { [id: string]: AssessmentTask });

                result.testObjects = audit.workflow.spec.testObjects || [];
                result.auditRequirements = audit.workflow.spec.requirements;

                return this.testService.getSpecs$(result.auditRequirements.map(req => req.specId)).pipe(
                    map(specs => ({audit, tasks, specs}))
                );
            }),
            /**
             * Get all stateArtifacts.
             */
            mergeMap(({audit, tasks, specs}) => {
                const stateIds = result.testObjects!
                    .map(obj => obj.target)
                    .filter((target): target is StateObservationTarget => target.type === 'StateObservationTarget')
                    .map(target => target.stateId);

                return this.artifactService.getArtifacts$(stateIds).pipe(
                    map(stateArtifacts => ({audit, tasks, specs, stateArtifacts}))
                );
            }),
            mergeMap(({audit, tasks, specs, stateArtifacts}) => {
                return this.testService.getFindingsStatsForAudit$(audit.projectId, this.auditId).pipe(
                    map(findingStatsBySpecIdAndTestObjectId => ({audit, tasks, specs, stateArtifacts, findingStatsBySpecIdAndTestObjectId}))
                );
            }),
            map(({audit, tasks, specs, stateArtifacts, findingStatsBySpecIdAndTestObjectId}) => {
                // Populate specs
                result.specs = specs.reduce((acc, spec) => {
                    acc[spec.id] = spec;
                    return acc;
                }, {} as { [specId: string]: Spec });

                // Populate states from stateArtifacts
                const states = stateArtifacts.reduce((acc: any, artifact: any) => {
                    acc[artifact!.id] = BrowserSessionStateArtifact.fromArtifact(artifact!);
                    return acc;
                }, {});

                // Map representative states by canonical test object id
                result.statesByTestObjectId = result.testObjects!.reduce((acc: any, obj) => {
                    if (obj.target.type === 'StateObservationTarget') {
                        acc[obj.testObjectId] = states[(obj.target as StateObservationTarget).stateId];
                    }
                    return acc;
                }, {});

                // Map test objects by id
                result.testObjectsById = result.testObjects!.reduce((acc, obj) => {
                    acc[obj.testObjectId] = obj;
                    return acc;
                }, {} as { [testObjectId: string]: HydratedAuditTarget });

                // Available test objects (unique state names)
                const seenStatenames = new Set<string>();
                result.availableTestObjects = result.testObjects!.reduce((acc, obj) => {
                    if (obj.target.type === 'StateObservationTarget') {
                        const stateName = obj.target.name || result.statesByTestObjectId![obj.testObjectId].name;
                        if (!seenStatenames.has(stateName)) {
                            seenStatenames.add(stateName);
                            acc.push({
                                label: stateName,
                                value: obj.testObjectId
                            });
                        }
                    }
                    if (obj.target.type === 'TagObservationTarget') {
                        acc.push({
                            label: obj.target.name,
                            value: obj.testObjectId
                        });
                    }
                    if (obj.target.type === 'ContextObservationTarget' || obj.target.type === 'URLObservationTarget') {
                        acc.push({
                            label: obj.target.name || obj.target.description || obj.testObjectId,
                            value: obj.testObjectId
                        });
                    }
                    return acc;
                }, [] as SelectItem[]);

                // Available tags
                const seenTags = new Set<string>();
                result.availableTags = Object.values(result.specs).reduce((acc, spec) => {
                    for (const tag of spec.labels || []) {
                        const label = tagToString(tag);
                        if (!seenTags.has(label)) {
                            seenTags.add(label);
                            acc.push({
                                label: label,
                                value: tag
                            });
                        }
                    }
                    return acc;
                }, [] as SelectItem[]);

                // Available execution statuses
                const seenExecutionStatuses = new Set<string>();
                result.availableExecutionStatuses = tasks.reduce((acc, task) => {
                    if (!seenExecutionStatuses.has(task.executionStatus)) {
                        seenExecutionStatuses.add(task.executionStatus);
                        acc.push({
                            label: task.executionStatus,
                            value: task.executionStatus
                        });
                    }
                    return acc;
                }, [] as SelectItem[]);

                // Available evaluation statuses
                const seenEvaluationStatuses = new Set<string>();
                result.availableEvaluationStatuses = tasks.reduce((acc, task) => {
                    if (!seenEvaluationStatuses.has(task.evaluationStatus)) {
                        seenEvaluationStatuses.add(task.evaluationStatus);
                        acc.push({
                            label: task.evaluationStatus,
                            value: task.evaluationStatus
                        });
                    }
                    return acc;
                }, [] as SelectItem[]);

                const sortedAssessmentTasks = result.assessmentTasks!.sort((a, b) => {
                    const aSpec = result.specs![a.assessedSpec];
                    const bSpec = result.specs![b.assessedSpec];
                    return alphanumericDisplayIdSort(aSpec?.displayId, bSpec?.displayId);
                });

                result.assessmentTasks = sortedAssessmentTasks;
                result.findingStatsBySpecIdAndTestObjectId = findingStatsBySpecIdAndTestObjectId || {};
                return result;
            })
        );
    }

    /**
     * Return all activities that match the given axe labels.
     * @param axeLabels
     */
    findSpecsForAxeLabels(axeLabels: string[]): Spec[] | undefined {
        const specs = Object.values(this.auditInfo.getValue().specs || {});
        if (!specs) {
            return undefined;
        }

        if (!axeLabels || axeLabels.length === 0 || specs.length === 0) {
            return [];
        }

        return specs.filter(spec => mapSpecLabelsToAxeLabels(spec.labels || []).some(label => axeLabels.includes(label)));
    }

    findAsessmentTasksForSpecs(specIds: SpecId[]): AssessmentTask[] | undefined {
        const auditInfo = this.auditInfo.getValue();
        if (!auditInfo) {
            return undefined;
        }

        if (!specIds || specIds.length === 0 || auditInfo.assessmentTasks!.length === 0) {
            return [];
        }

        return auditInfo.assessmentTasks!.filter(activity => specIds.includes(activity.assessedSpec));
    }


    getFindingIdsForActivity(activityId: AuditActivityId): Observable<FindingId[]> {
        const auditInfo = this.auditInfo.getValue();
        if (!auditInfo) {
            return of([]);
        }

        const task = auditInfo.idToAssessmentTask?.[activityId];
        if (!task) {
            return of([]);
        }

        const spec = task.assessedSpec;
        const testObjectId = task.testObjectId;
        if (!testObjectId) {
            return of([]);
        }

        const projectId = auditInfo.audit?.projectId;
        if (!projectId) {
            return of([]);
        }

        return this.testService.queryFindings$(projectId, {
            filterSpec: {
                auditFilterSpec: [this.auditId],
                affectedSpecs: [spec],
                testObjectIds: [testObjectId],
            }
        }).pipe(
            take(1),
            map(findingResults => {
                return findingResults.data.items.map(r => r.id);
            })
        );
    }

    getFindingsForActivity(activityId: AuditActivityId): Observable<Finding[] | undefined> {
        return this.getFindingIdsForActivity(activityId).pipe(
            switchMap(findinIds => {
                return this.testService.getFindings$(findinIds).pipe(take(1))
            }),
            catchError(err => {
                console.error('Error fetching findings for activity', activityId, err);
                return of([]);
            }),
            take(1)
        );
    }

    updateAuditInfo() {
        this.updateAuditInfo$.next();
    }

    getObservationTargetsForActivity(activity?: AuditActivity): ObservationTarget[]  {
        if (!activity) {
            return [];
        } else {
            return (this.auditInfo.getValue().testObjects || [])
                .filter(o => o.testObjectId === activity.testObjectId)
                .map(o => o.target);
        }
    }

    /**
     * Computes a list of 'AssessmentTask' for a given 'Audit'. This function ensures that each required
     * combination of `TestObjectId` and `SpecId` in the `planningMatrix` has a corresponding `AssessmentTask`.
     * Existing tasks are retrieved from the backend, and missing tasks are created as flyweight object placeholders.
     *
     * @param {Audit} audit - The audit object for which the assessment tasks are computed.
     * @returns {Observable<AssessmentTask[]>} - An observable emitting an array of `AssessmentTask`s.
     */
    private computeAssessmentTasks(audit: Audit): Observable<AssessmentTask[]> {
        if (audit?.workflow?.type === 'RequirementsWorkflow') {
            const assessmentPairs = this.calculateAssessmentPairsFromPlanningMatrix(audit.workflow.spec.planningMatrix);
            return this.auditService.getAuditActivitiesOfAudit$(audit.id!).pipe(
                map(activities => {
                    /**
                     * Transform activites from the list:
                     * [
                     *     {type: AssessmentTask, testObjectId: "www.testfabrik.com", assessedSpec: "1.1.1" }
                     *     {type: AssessmentTask, testObjectId: "www.testfabrik.com", assessedSpec: "1.2" }
                     * ]
                     *
                     * to a Dictionary of combined AssesementsTasks:
                     * {
                     *     "www.testfabrik.com-1.1.1": {type: AssessmentTask, testObjectId: "www.testfabrik.com", assessedSpec: "1.1.1"},
                     *     "www.testfabrik.com-1.2: {type: AssessmentTask, testObjectId: "www.testfabrik.com", assessedSpec: "1.2" }
                     * }
                     */
                    const backendAssessmentTasks = activities.reduce((acc, activity) => {
                        // Filter out only AssesementsTasks which get accumulated inside the 'acc' Accumulator
                        if (activity.type === 'AssessmentTask') {
                            const activityTargetId = activity.testObjectId;
                            acc[activityTargetId + '-' + activity.assessedSpec] = activity;
                        }
                        return acc;
                    }, {} as { [key: string]: AuditActivity });

                    /**
                     * We loop through all needed AssessmentTasks we created earlier locally.
                     * We check if they already exist in the backend or not with the backendAssesemntTask Dictionary.
                     * If they exist we take the already saved one from the backend.
                     * If not we create the needed flyweight object for that Assesement Task that we will use.
                     */
                    const resultTasks: AssessmentTask[] = [];
                    for (const pair of assessmentPairs) {
                        if (backendAssessmentTasks[pair.obj + '-' + pair.spec]) {
                            resultTasks.push(backendAssessmentTasks[pair.obj + '-' + pair.spec] as AssessmentTask);
                        } else {
                            /**
                             * these are flyweight (pattern) objects in the backend (They only will be created in
                             * the backend when changes are made to it.
                             **/
                            resultTasks.push({
                                needsReification: true,
                                testObjectId: pair.obj,
                                assessedSpec: pair.spec,
                                type: 'AssessmentTask',
                                id: AuditService.generateFlyweightActivityId(pair.obj, pair.spec),
                                auditId: audit.id!,
                                evaluationStatus: 'undecided',
                                executionStatus: 'notstarted',
                                hiddenFindings: [],
                                summary: ''
                            } as AssessmentTask /* needed because excess property check vs 'needsReification' */);
                        }
                    }
                    return resultTasks;
                })
            );
        } else {
            return of([]);
        }
    }

    /**
     * Calculates all possible pairs of `TestObjectId` and `SpecId` from a given `PlanningMatrix`.
     * This function takes a mapping matrix and generates an array of objects,
     * where each object combines a `TestObjectId` with a `SpecId`.
     *
     * @param matrix An object mapping each `TestObjectId` (as a key) to a list of `SpecId`s (as an array).
     * @returns {Array<{ obj: TestObjectId, spec: SpecId }>} - An array of objects, where each object represents a
     * combination of `TestObjectId` and `SpecId`.
     */
    private calculateAssessmentPairsFromPlanningMatrix(matrix: PlanningMatrix): { obj: TestObjectId, spec: SpecId }[] {
        /**
         *  How the 'PlanningMatrix' is build:
         *  {
         *      "Testfabrik.com": ["1.1.1", "1.2.1"],
         *      "Google.com": ["1.1.1"]
         *  }
         *
         *  Will be transfomed into:
         *  [
         *      { obj: ""Testfabrik.com"", spec: "1.1.1" },
         *      { obj: ""Testfabrik.com"", spec: "1.2.1" },
         *      { obj: "Google.com", spec: "1.1.1" }
         *  ]
         */
        let activities = [];
        for (const [objId, specs] of Object.entries(matrix)) {
            for (const specId of specs) {
                activities.push({
                    obj: objId,
                    spec: specId
                });
            }
        }
        return activities;
    }
}
